import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Image, Modal, Platform, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { JackpotChipPng, SmallVipSix } from '../../../assets';
import CloseIcon from '../../../assets/dailyMissions/close.svg';
import { checkDonation, DonationRecipient } from '../../../services/donationsApi';
import { goldGlow } from '../../../shared/glowStyles';
import { Package } from '../../../store/types';
import { useDevice } from '../../../utils/hooks/useDevice';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { CtaButton } from '../common/CtaButton';
import { Pressable } from '../common/Touchable';
import DonationToText from '../Store/DonationToText';
import { formatAmount, formatPrice } from '../Store/StoreRedesignModal';
import { PaymentProvider } from './PaymentCheckoutModal';
import { useRedesignScale } from './useRedesignScale';

// The step between picking a donation and paying for it, in the checkout sheet's chrome
// (PaymentCheckoutModal): who receives it, what, for how much and how it is paid, and what it
// unlocks. It asks the backend first whether the donation is allowed - mandatory before an in-app
// purchase, since the stores charge before the backend ever sees it - and only then lets the
// player pay. Laid out on the 390-wide mobile frame and scaled to the viewport once.
const DESIGN_FRAME_WIDTH = 390;
const DESIGN_MODAL_WIDTH = 358;
const DESIGN_MODAL_HEIGHT = 520;
const MAX_SCALE = 1.15;
const VIEWPORT_MARGIN = 48;

const FONT = 'Sofia Sans';

const colors = {
	backdrop: 'rgba(0,0,0,0.68)',
	panelBorder: '#FFF908',
	panelBase: '#320400',
	surface: '#1A0603',
	white: '#FFFFFF',
	gold: '#FFC500',
	amount: '#FFE275',
	tileFill: '#330E07',
	tileBorder: '#DA600E',
	muted: '#D9C4BC',
	error: '#FF9B8A',
} as const;

const PANEL_FILL: Array<[string, string]> = [
	['0', '#770700'],
	['0.80127', '#320400'],
	['1', '#320400'],
];

type CheckState =
	| { status: 'checking' }
	| { status: 'allowed' }
	| { status: 'refused'; reason: string }
	| { status: 'failed' };

type Props = {
	pkg: Package | null;
	recipient: DonationRecipient;
	/** The method picked in the checkout sheet on web; null in the apps, which pay in-store. */
	provider: PaymentProvider | null;
	onConfirm: (pkg: Package) => void;
	onClose: () => void;
	/**
	 * Pays from here instead, once the donation check passed - Stripe's own Link button on web -
	 * given the Confirm button and the slot's height, which it keeps as the fallback.
	 */
	renderPayButton?: (fallback: React.ReactNode, height: number) => React.ReactNode;
};

const DonationConfirmModal = ({
	pkg,
	recipient,
	provider,
	onConfirm,
	onClose,
	renderPayButton,
}: Props) => {
	const { t } = useTranslation();
	const { width, height } = useSafeViewport();
	const { isDesktop } = useDevice();
	const panelId = useSvgId('donationConfirmPanel');
	const [check, setCheck] = useState<CheckState>({ status: 'checking' });

	const desktopModalScale = useRedesignScale({ fitHeight: DESIGN_MODAL_HEIGHT });
	const scale = isDesktop
		? Math.min(desktopModalScale, (height - VIEWPORT_MARGIN) / DESIGN_MODAL_HEIGHT)
		: Math.min(
				width / DESIGN_FRAME_WIDTH,
				(height - VIEWPORT_MARGIN) / DESIGN_MODAL_HEIGHT,
				MAX_SCALE,
			);
	const styles = useMemo(() => createStyles(scale), [scale]);

	const packageId = pkg?.id;
	const runCheck = useCallback(() => {
		if (!packageId) return () => undefined;

		let cancelled = false;
		setCheck({ status: 'checking' });
		checkDonation({ recipientPlayerId: recipient.playerId, packageId })
			.then(result => {
				if (cancelled) return;
				setCheck(
					result.allowed
						? { status: 'allowed' }
						: {
								status: 'refused',
								reason:
									result.reason ||
									t('T_SITE_DONATION_NOT_ALLOWED', {
										defaultValue: 'This donation is not possible right now.',
									}),
							},
				);
			})
			.catch(error => {
				if (cancelled) return;
				console.warn('[DonationConfirm] Donation check failed:', error?.message);
				setCheck({ status: 'failed' });
			});

		return () => {
			cancelled = true;
		};
	}, [packageId, recipient.playerId, t]);

	// Every package the sheet opens on is checked afresh.
	useEffect(() => runCheck(), [runCheck]);

	if (!pkg) return null;

	const isVip = pkg.type === 'VIP_MEMBERSHIP';
	const username = recipient.username;
	const amount = pkg.chipAmount ?? pkg.chips ?? 0;
	const days = pkg.vipDurationDays ?? 0;
	const packageLabel = isVip
		? t('T_SITE_DONATION_VIP_DAYS', { defaultValue: '{{days}} days of VIP', days })
		: t('T_SITE_REFERRAL_REWARD_CHIPS', {
				defaultValue: '{{amount}} Chips',
				amount: formatAmount(amount),
			});

	const paymentMethod = (() => {
		switch (provider) {
			case 'stripe':
				return t('T_SITE_PAYMENT_METHOD_CARD_SHORT', { defaultValue: 'Card' });
			case 'link':
				return 'Link';
			case 'paypal':
				return 'PayPal';
			case 'epay':
				return 'ePay.bg';
			case 'easypay':
				return 'EasyPay';
			default:
				return Platform.OS === 'ios'
					? t('T_SITE_DONATION_APP_STORE', { defaultValue: 'App Store' })
					: t('T_SITE_DONATION_GOOGLE_PLAY', { defaultValue: 'Google Play' });
		}
	})();

	const details: Array<{ key: string; label: string; value: string }> = [
		{
			key: 'recipient',
			label: t('T_SITE_DONATION_RECIPIENT', { defaultValue: 'Recipient' }),
			value: username,
		},
		{
			key: 'package',
			label: t('T_SITE_DONATION_PACKAGE', { defaultValue: 'Package' }),
			value: packageLabel,
		},
		{
			key: 'price',
			label: t('T_SITE_DONATION_PRICE', { defaultValue: 'Price' }),
			value: formatPrice(pkg),
		},
		{
			key: 'method',
			label: t('T_SITE_DONATION_PAYMENT_METHOD', { defaultValue: 'Payment method' }),
			value: paymentMethod,
		},
	];

	const notes = [
		t('T_SITE_DONATION_CONFIRM_RECEIVES', {
			defaultValue: '{{username}} receives the donation.',
			username,
		}),
		t('T_SITE_DONATION_CONFIRM_UNLOCKS', {
			defaultValue: 'Completing it unlocks a private chat with {{username}}.',
			username,
		}),
		...(isVip
			? [
					t('T_SITE_DONATION_CONFIRM_ONE_TIME', {
						defaultValue: 'One-time purchase, not a subscription.',
					}),
				]
			: []),
	];

	const ctaLabel = isVip
		? t('T_SITE_DONATION_DONATE_VIP', { defaultValue: 'DONATE VIP' })
		: t('T_SITE_PROFILE_DONATE_CHIPS', { defaultValue: 'DONATE CHIPS' });

	const confirmButton = (
		<CtaButton
			label={ctaLabel}
			variant='success'
			disabled={check.status === 'checking' || check.status === 'refused'}
			// A check that never answered is asked again rather than skipped.
			onPress={check.status === 'failed' ? () => runCheck() : () => onConfirm(pkg)}
			style={styles.action}
			labelStyle={styles.actionLabel}
		/>
	);

	return (
		<Modal
			visible
			transparent
			animationType='fade'
			onRequestClose={onClose}
			statusBarTranslucent
			navigationBarTranslucent
		>
			<View style={styles.overlay}>
				<Pressable accessibilityRole='button' onPress={onClose} style={styles.backdrop} />

				<View style={styles.panel}>
					<View pointerEvents='none' style={styles.panelFill}>
						<Svg width='100%' height='100%'>
							<Defs>
								<LinearGradient id={panelId} x1='0' y1='0' x2='0' y2='1'>
									{PANEL_FILL.map(([offset, color]) => (
										<Stop key={offset} offset={offset} stopColor={color} />
									))}
								</LinearGradient>
							</Defs>
							<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${panelId})`} />
						</Svg>
					</View>

					<Pressable
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_CLOSE', { defaultValue: 'Close' })}
						onPress={onClose}
						style={styles.closeHitArea}
						hitSlop={8}
					>
						<View style={styles.closeCircle}>
							<CloseIcon width={18 * scale} height={18 * scale} />
						</View>
					</Pressable>

					<Text style={styles.eyebrow}>
						{t('T_SITE_DONATION_CONFIRM_TITLE', { defaultValue: 'Confirm your donation' })}
					</Text>
					<DonationToText
						username={username}
						numberOfLines={2}
						usernameFontSize={28 * scale}
						style={styles.heading}
					/>

					<View style={styles.packageBanner}>
						<Image
							source={isVip ? SmallVipSix : JackpotChipPng}
							style={styles.packageIcon}
							resizeMode='contain'
						/>
						<Text numberOfLines={1} style={styles.packageAmount}>
							{packageLabel}
						</Text>
					</View>

					<View style={styles.details}>
						{details.map(row => (
							<View key={row.key} style={styles.detailRow}>
								<Text numberOfLines={1} style={styles.detailLabel}>
									{row.label}
								</Text>
								<Text numberOfLines={1} style={styles.detailValue}>
									{row.value}
								</Text>
							</View>
						))}
					</View>

					<View style={styles.notes}>
						{notes.map(note => (
							<Text key={note} style={styles.note}>
								{note}
							</Text>
						))}
					</View>

					{check.status === 'checking' ? (
						<View style={styles.checking}>
							<ActivityIndicator color={colors.white} />
						</View>
					) : null}
					{check.status === 'refused' ? <Text style={styles.error}>{check.reason}</Text> : null}
					{check.status === 'failed' ? (
						<Text style={styles.error}>
							{t('T_SITE_DONATION_CHECK_FAILED', {
								defaultValue: 'We could not confirm this donation. Please try again.',
							})}
						</Text>
					) : null}

					{renderPayButton && check.status === 'allowed' ? (
						<View style={styles.paySlot}>{renderPayButton(confirmButton, 44 * scale)}</View>
					) : (
						confirmButton
					)}
				</View>
			</View>
		</Modal>
	);
};

const createStyles = (scale: number) => {
	const s = (value: number) => value * scale;

	return StyleSheet.create({
		overlay: {
			flex: 1,
			alignItems: 'center',
			justifyContent: 'center',
		},
		backdrop: {
			...StyleSheet.absoluteFillObject,
			backgroundColor: colors.backdrop,
		},
		panel: {
			width: s(DESIGN_MODAL_WIDTH),
			paddingHorizontal: s(16),
			paddingTop: s(24),
			paddingBottom: s(20),
			alignItems: 'center',
			borderRadius: s(16),
			borderWidth: Math.max(1, s(1.5)),
			borderColor: colors.panelBorder,
			// The gradient fill sits on top of this; the flat colour keeps the shadow layer opaque.
			backgroundColor: colors.panelBase,
			shadowColor: '#FFB800',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.45,
			shadowRadius: s(8),
			elevation: 12,
		},
		panelFill: {
			...StyleSheet.absoluteFillObject,
			borderRadius: s(16),
			overflow: 'hidden',
		},
		closeHitArea: {
			position: 'absolute',
			top: s(8),
			right: s(8),
			width: s(44),
			height: s(44),
			alignItems: 'center',
			justifyContent: 'center',
			zIndex: 2,
		},
		closeCircle: {
			width: s(32),
			height: s(32),
			borderRadius: s(16),
			borderWidth: Math.max(0.5, s(1)),
			borderColor: colors.panelBorder,
			backgroundColor: colors.surface,
			alignItems: 'center',
			justifyContent: 'center',
			...goldGlow,
		},
		eyebrow: {
			fontFamily: FONT,
			fontSize: s(14),
			lineHeight: s(18),
			fontWeight: '600',
			color: colors.muted,
			textAlign: 'center',
			marginHorizontal: s(40),
		},
		heading: {
			marginTop: s(4),
			marginHorizontal: s(28),
			fontFamily: FONT,
			fontSize: s(24),
			lineHeight: s(33),
			fontWeight: '800',
			color: colors.amount,
			textAlign: 'center',
			textShadowColor: 'rgba(255, 156, 0, 0.5)',
			textShadowOffset: { width: 0, height: 1 },
			textShadowRadius: 4,
		},
		packageBanner: {
			marginTop: s(14),
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			gap: s(8),
		},
		packageIcon: {
			width: s(30),
			height: s(30),
		},
		packageAmount: {
			flexShrink: 1,
			fontFamily: FONT,
			fontSize: s(28),
			lineHeight: s(34),
			fontWeight: '800',
			color: colors.amount,
		},
		details: {
			alignSelf: 'stretch',
			marginTop: s(14),
			paddingHorizontal: s(12),
			paddingVertical: s(8),
			borderRadius: s(12),
			borderWidth: Math.max(0.5, s(1)),
			borderColor: colors.tileBorder,
			backgroundColor: colors.tileFill,
		},
		detailRow: {
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
			gap: s(12),
			paddingVertical: s(4),
		},
		detailLabel: {
			fontFamily: FONT,
			fontSize: s(15),
			lineHeight: s(19),
			fontWeight: '500',
			color: colors.muted,
		},
		detailValue: {
			flexShrink: 1,
			fontFamily: FONT,
			fontSize: s(15),
			lineHeight: s(19),
			fontWeight: '700',
			color: colors.white,
			textAlign: 'right',
		},
		notes: {
			alignSelf: 'stretch',
			marginTop: s(12),
			gap: s(4),
		},
		note: {
			fontFamily: FONT,
			fontSize: s(14),
			lineHeight: s(19),
			fontWeight: '500',
			color: colors.white,
			textAlign: 'center',
		},
		checking: {
			marginTop: s(12),
			height: s(20),
			justifyContent: 'center',
		},
		error: {
			marginTop: s(12),
			fontFamily: FONT,
			fontSize: s(14),
			lineHeight: s(18),
			fontWeight: '600',
			color: colors.error,
			textAlign: 'center',
		},
		action: {
			marginTop: s(16),
			alignSelf: 'stretch',
			minHeight: s(44),
		},
		paySlot: {
			marginTop: s(16),
			alignSelf: 'stretch',
		},
		actionLabel: {
			fontSize: s(17),
		},
	});
};

export default DonationConfirmModal;
